import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, FileText, CheckCircle, Clock, MapPin, Settings, DollarSign, Newspaper, Layers } from 'lucide-react';
import Header from '../../components/Header';
import { Button } from '@/components/ui/button';
import { dashboardAPI } from '../../utils/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const appointmentStats = [
    {
      label: 'Total Appointments',
      value: stats?.appointments?.total || 0,
      icon: Calendar,
      color: 'bg-blue-500'
    },
    {
      label: 'Pending',
      value: stats?.appointments?.pending || 0,
      icon: Clock,
      color: 'bg-yellow-500'
    },
    {
      label: 'Approved',
      value: stats?.appointments?.approved || 0,
      icon: CheckCircle,
      color: 'bg-green-500'
    },
    {
      label: 'Completed',
      value: stats?.appointments?.completed || 0,
      icon: CheckCircle,
      color: 'bg-purple-500'
    }
  ];

  const grievanceStats = [
    {
      label: 'Total Grievances',
      value: stats?.grievances?.total || 0,
      icon: FileText,
      color: 'bg-blue-500'
    },
    {
      label: 'Pending',
      value: stats?.grievances?.pending || 0,
      icon: Clock,
      color: 'bg-yellow-500'
    },
    {
      label: 'In Progress',
      value: stats?.grievances?.in_progress || 0,
      icon: Clock,
      color: 'bg-orange-500'
    },
    {
      label: 'Resolved',
      value: stats?.grievances?.resolved || 0,
      icon: CheckCircle,
      color: 'bg-green-500'
    }
  ];

  const configModules = [
    {
      title: 'Centers',
      description: 'Manage service centers and locations',
      icon: MapPin,
      path: '/admin/centers',
      color: 'bg-blue-500'
    },
    {
      title: 'Services',
      description: 'Configure service types and sub-services',
      icon: Layers,
      path: '/admin/services',
      color: 'bg-purple-500'
    },
    {
      title: 'Fee Structure',
      description: 'Set fees by nationality and service',
      icon: DollarSign,
      path: '/admin/fees',
      color: 'bg-green-500'
    },
    {
      title: 'Appointment Config',
      description: 'Configure booking rules and capacity',
      icon: Settings,
      path: '/admin/appointment-config',
      color: 'bg-amber-500'
    },
    {
      title: 'Content',
      description: 'Manage news and testimonials',
      icon: Newspaper,
      path: '/admin/content',
      color: 'bg-pink-500'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-secondary">
      <Header />
      
      <div className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-primary mb-2">
              Admin Dashboard
            </h1>
            <p className="text-muted-foreground">
              Welcome back, Administrator
            </p>
          </div>

          {/* Configuration Modules */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-primary mb-6">
              Configuration
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {configModules.map((module, index) => (
                <div
                  key={index}
                  onClick={() => navigate(module.path)}
                  className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm hover:shadow-md transition-all cursor-pointer hover:-translate-y-1"
                  data-testid={`config-module-${index}`}
                >
                  <div className={`w-10 h-10 rounded-lg ${module.color} flex items-center justify-center mb-3`}>
                    <module.icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-primary mb-1">{module.title}</h3>
                  <p className="text-xs text-muted-foreground">{module.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Appointments Section */}
          <div className="mb-12">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-primary">
                Appointments
              </h2>
              <Button
                onClick={() => navigate('/admin/appointments')}
                data-testid="view-all-appointments-btn"
              >
                View All
              </Button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {appointmentStats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`appointment-stat-${index}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-primary mb-1 font-mono">
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Grievances Section */}
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-primary">
                Grievances
              </h2>
              <Button
                onClick={() => navigate('/admin/grievances')}
                data-testid="view-all-grievances-btn"
              >
                View All
              </Button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {grievanceStats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`grievance-stat-${index}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-primary mb-1 font-mono">
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
